import type {NewTeachersAssignScope} from 'common/components/dataUpload/NewTeachersModal'
import type {DataUploadNewStudent, DataUploadNewTeacher} from 'common/helpers/dataUpload'
import {GraduationCap, UserPlus} from 'lucide-react'
import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

/** Students listed in the dialog; the rest are counted ("…and 40 more"). */
const MAX_LISTED_STUDENTS = 100

/** What happens to the file's new teachers. */
export type NewTeachersChoice = 'assign' | 'only' | 'none'
/** What happens to the file's new students. */
export type NewStudentsChoice = 'only' | 'choose' | 'none'

export interface NewPeopleAnswer {
  teachers: NewTeachersChoice
  /** The ticked teachers' keys (`approvedNewTeachers`), in the server's order. */
  teacherKeys: string[]
  students: NewStudentsChoice
}

const ASSIGN_LABELS = {
  lines: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_LINES',
  classes: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_CLASSES',
  everything: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_ALL',
} satisfies Record<NewTeachersAssignScope, string>

interface Props {
  show: boolean
  teachers: readonly DataUploadNewTeacher[]
  /** The rest of the file imports without the teachers' lines ("Don't save" leaves them out). */
  canContinueWithout: boolean
  /** What "Save and assign" gives the teachers (its label). */
  assignScope: NewTeachersAssignScope
  students: readonly DataUploadNewStudent[]
  /** The file is only a students list: its students are all it holds for this step. */
  studentsOnly?: boolean
  submitting?: boolean
  onConfirm: (answer: NewPeopleAnswer) => void
  /** Nobody is saved. */
  onCancel: () => void
}

/**
 * The people an upload names that the school has not saved, in one dialog: its teachers
 * (allocation lines wait on them) and its students (another list in the same workbook). Each
 * group has its own choice — teachers: save and assign (default), save only, or not; students:
 * save only (default), save and choose their classes and subjects on the Students step, or not —
 * and one Continue applies both.
 */
const NewPeopleModal: FC<Props> = ({
  show,
  teachers,
  canContinueWithout,
  assignScope,
  students,
  studentsOnly = false,
  submitting = false,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl()
  const [picked, setPicked] = useState<Set<string>>(() => new Set())
  const [teacherChoice, setTeacherChoice] = useState<NewTeachersChoice>('assign')
  const [studentChoice, setStudentChoice] = useState<NewStudentsChoice>('only')

  // Each time it opens: every teacher ticked, and the default choices.
  useEffect(() => {
    if (!show) return
    setPicked(new Set(teachers.map((teacher) => teacher.key)))
    setTeacherChoice('assign')
    setStudentChoice('only')
  }, [show, teachers])

  const toggle = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const hasTeachers = teachers.length > 0
  const hasStudents = students.length > 0
  const savesTeachers = hasTeachers && teacherChoice !== 'none'
  const listed = students.slice(0, MAX_LISTED_STUDENTS)
  const more = students.length - listed.length

  /** The choice buttons of one group. */
  const choices = <T extends string>(
    options: Array<{value: T; label: string}>,
    value: T,
    onChange: (next: T) => void
  ) => (
    <div className='d-flex flex-wrap gap-2 mb-2' role='radiogroup'>
      {options.map((option) => (
        <button
          key={option.value}
          type='button'
          role='radio'
          aria-checked={value === option.value}
          disabled={submitting}
          className={`btn btn-sm ${value === option.value ? 'btn-primary' : 'btn-light'}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onCancel}
      centered
      size='lg'
      backdrop={submitting ? 'static' : undefined}
    >
      <Modal.Header closeButton={!submitting} className='border-0 pb-0'>
        <Modal.Title className='fs-3'>
          {intl.formatMessage({
            id:
              hasTeachers && hasStudents
                ? 'DATA_UPLOAD.NEW_PEOPLE.TITLE'
                : hasTeachers
                  ? 'DATA_UPLOAD.NEW_TEACHERS.TITLE'
                  : 'DATA_UPLOAD.NEW_PEOPLE.STUDENTS_TITLE',
          })}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='pt-4' style={{maxHeight: '65vh', overflowY: 'auto'}}>
        {hasTeachers && (
          <section className='mb-6'>
            <div className='d-flex align-items-center gap-2 fw-bold fs-5'>
              <UserPlus size={18} className='text-primary' aria-hidden />
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.NEW_PEOPLE.TEACHERS_FOUND'},
                {count: teachers.length}
              )}
            </div>
            <p className='sheet-assign-hint mt-1 mb-3'>
              {intl.formatMessage({
                id: canContinueWithout
                  ? 'DATA_UPLOAD.NEW_TEACHERS.HINT_SOME'
                  : 'DATA_UPLOAD.NEW_TEACHERS.HINT_ALL',
              })}
            </p>
            {choices<NewTeachersChoice>(
              [
                {value: 'assign', label: intl.formatMessage({id: ASSIGN_LABELS[assignScope]})},
                {
                  value: 'only',
                  label: intl.formatMessage(
                    {id: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ONLY'},
                    {count: picked.size}
                  ),
                },
                {
                  value: 'none',
                  label: intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.DONT_SAVE'}),
                },
              ],
              teacherChoice,
              setTeacherChoice
            )}
            <p className='text-muted fs-8 mb-3'>
              {intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.OPTIONS_NOTE_LINES'})}
            </p>
            {savesTeachers &&
              teachers.map((teacher) => {
                const checked = picked.has(teacher.key)
                const inputId = `new-person-teacher-${teacher.key}`
                return (
                  <div
                    key={teacher.key}
                    className={`sheet-assign-card${checked ? ' is-answered' : ''}`}
                  >
                    <div className='d-flex align-items-start gap-3'>
                      <input
                        id={inputId}
                        type='checkbox'
                        className='form-check-input mt-1 flex-shrink-0'
                        checked={checked}
                        disabled={submitting}
                        onChange={() => toggle(teacher.key)}
                      />
                      <label htmlFor={inputId} className='flex-grow-1' style={{minWidth: 0}}>
                        <span className='d-flex flex-wrap align-items-center gap-2'>
                          <span className='sheet-assign-name'>{teacher.name}</span>
                          {teacher.employeeId && (
                            <span className='badge badge-light fs-8 fw-semibold'>
                              {teacher.employeeId}
                            </span>
                          )}
                          {teacher.lines > 0 && (
                            <span className='badge badge-light-primary fs-8 fw-semibold'>
                              {intl.formatMessage(
                                {id: 'DATA_UPLOAD.NEW_TEACHERS.LINES'},
                                {count: teacher.lines}
                              )}
                            </span>
                          )}
                        </span>
                        {teacher.samples.length > 0 && (
                          <span className='d-block text-muted fs-7 mt-1'>
                            {teacher.samples.join(' · ')}
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                )
              })}
          </section>
        )}

        {hasStudents && (
          <section>
            <div className='d-flex align-items-center gap-2 fw-bold fs-5'>
              <GraduationCap size={18} className='text-primary' aria-hidden />
              {intl.formatMessage({id: 'DATA_UPLOAD.NEW_STUDENTS.TITLE'}, {count: students.length})}
            </div>
            <p className='sheet-assign-hint mt-1 mb-3'>
              {intl.formatMessage({
                id: studentsOnly
                  ? 'DATA_UPLOAD.NEW_STUDENTS.HINT'
                  : 'DATA_UPLOAD.NEW_STUDENTS.HINT_ALSO',
              })}
            </p>
            {choices<NewStudentsChoice>(
              [
                {
                  value: 'only',
                  label: intl.formatMessage({id: 'DATA_UPLOAD.NEW_STUDENTS.SAVE_ONLY'}),
                },
                {
                  value: 'choose',
                  label: intl.formatMessage({id: 'DATA_UPLOAD.NEW_STUDENTS.SAVE_CHOOSE'}),
                },
                {
                  value: 'none',
                  label: intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.DONT_SAVE'}),
                },
              ],
              studentChoice,
              setStudentChoice
            )}
            {studentChoice !== 'none' && (
              <p className='text-muted fs-8 mb-3'>
                {intl.formatMessage({
                  id:
                    studentChoice === 'only'
                      ? 'DATA_UPLOAD.NEW_STUDENTS.ONLY_HINT'
                      : 'DATA_UPLOAD.NEW_STUDENTS.CHOOSE_HINT',
                })}
              </p>
            )}
            {studentChoice !== 'none' && (
              <>
                {listed.map((student, index) => (
                  <div
                    key={`${student.registrationNumber ?? ''}-${student.name}-${index}`}
                    className='sheet-assign-card'
                  >
                    <span className='d-flex flex-wrap align-items-center gap-2'>
                      <span className='sheet-assign-name'>{student.name}</span>
                      {student.registrationNumber && (
                        <span className='badge badge-light fs-8 fw-semibold'>
                          {student.registrationNumber}
                        </span>
                      )}
                      {student.class && (
                        <span className='badge badge-light-primary fs-8 fw-semibold'>
                          {[student.class, student.section].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </span>
                    {student.subjects.length > 0 && (
                      <span className='d-block text-muted fs-7 mt-1'>
                        {student.subjects.join(' · ')}
                      </span>
                    )}
                  </div>
                ))}
                {more > 0 && (
                  <p className='text-muted fs-7 mb-0'>
                    {intl.formatMessage({id: 'DATA_UPLOAD.NEW_STUDENTS.MORE'}, {count: more})}
                  </p>
                )}
              </>
            )}
          </section>
        )}
      </Modal.Body>

      <Modal.Footer className='border-0 d-flex justify-content-end gap-2'>
        <button type='button' className='btn btn-light' disabled={submitting} onClick={onCancel}>
          {intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.DONT_SAVE'})}
        </button>
        <button
          type='button'
          className='btn btn-primary d-inline-flex align-items-center gap-2'
          // Teachers to save need at least one ticked.
          disabled={submitting || (savesTeachers && picked.size === 0)}
          onClick={() =>
            onConfirm({
              teachers: hasTeachers ? teacherChoice : 'none',
              teacherKeys: savesTeachers
                ? teachers.map((teacher) => teacher.key).filter((key) => picked.has(key))
                : [],
              students: hasStudents ? studentChoice : 'none',
            })
          }
        >
          {submitting && (
            <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
          )}
          {intl.formatMessage({id: 'DATA_UPLOAD.NEW_PEOPLE.CONTINUE'})}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default NewPeopleModal
